import { cva } from 'class-variance-authority';

/**
 * One CVA per role — container, entry, badge — with a `layout` variant.
 *
 * States are written once in the shared base and only the box changes between
 * the three structures; three separate components would have tripled them.
 */
export const appNavStyles = {
  root: cva('z-50 text-fg-muted', {
    variants: {
      layout: {
        tabbar: [
          'fixed inset-x-0 bottom-0 flex items-center justify-around gap-1 px-1.5',
          // Without `env(safe-area-inset-bottom)` the bar slides under the iOS
          // gesture bar. `viewport-fit=cover` is required in index.html.
          'h-16 pb-[env(safe-area-inset-bottom)]',
          'border-t border-border bg-nav/90 backdrop-blur-xl',
        ],
        topbar: [
          'sticky top-0 flex h-14 items-center gap-6 px-6',
          'border-b border-border bg-nav/60 backdrop-blur-xl',
        ],
        rail: [
          'sticky top-0 flex h-dvh w-55 shrink-0 flex-col gap-1',
          'border-r border-border bg-nav/35 px-3.5 py-5',
        ],
      },
    },
  }),

  item: cva(
    [
      'group relative flex items-center rounded-panel font-medium outline-none',
      'transition-colors duration-150',
      // In Tailwind 4, `hover:` is already wrapped in `@media (hover: hover)`,
      // so no sticky hover after a tap.
      'hover:bg-surface-hover hover:text-fg',
      'focus-visible:ring-2 focus-visible:ring-accent/60',
      // The active state is driven by `aria-current`, which <NavLink> sets on
      // its own: no `isActive` prop, so the visual cannot diverge from what a
      // screen reader announces.
      //
      // Warning: the active COLOUR is declared in each variant, never here. CVA
      // only concatenates, so a colour here and another in a variant give two
      // classes of equal specificity, and stylesheet order decides.
    ],
    {
      variants: {
        layout: {
          // Icons only: the target keeps its 44 px, it just has no text to fit.
          tabbar: [
            'h-11 flex-1 justify-center',
            'aria-[current=page]:text-accent',
          ],
          topbar: [
            'gap-2 px-2 py-1.5 text-sm',
            'aria-[current=page]:font-semibold aria-[current=page]:text-fg',
            // Soulignement actif en `::after`. Rappel utile : c'est
            // exactement l'effet qui n'existait pas en React Native.
            'after:absolute after:inset-x-2 after:-bottom-2.5 after:h-0.5',
            'after:rounded-full after:bg-accent after:opacity-0',
            'after:transition-opacity aria-[current=page]:after:opacity-100',
          ],
          // The same active state as the settings navigation, which is what the
          // mockup writes for both.
          rail: [
            'gap-3 px-2.5 py-2.5 text-sm',
            'aria-[current=page]:bg-accent-soft aria-[current=page]:font-semibold',
            'aria-[current=page]:text-fg aria-[current=page]:[&_svg]:text-accent',
          ],
        },
      },
    },
  ),

  icon: cva('shrink-0', {
    variants: {
      // With no label under it, the bottom-bar icon carries the meaning alone:
      // so it is one step larger than in the rail.
      layout: { tabbar: 'size-6', topbar: 'size-4', rail: 'size-5' },
    },
  }),

  badge: cva(
    [
      'grid min-w-4 place-items-center rounded-full px-1',
      'bg-accent text-4xs font-bold text-on-accent tabular-nums',
    ],
    {
      variants: {
        layout: {
          tabbar: 'absolute top-0.5 right-1/4 h-4',
          topbar: 'absolute -top-1 -right-1 h-4',
          rail: 'ml-auto h-4.5',
        },
      },
    },
  ),

  /**
   * The central scan button — the mockups' gradient, in native CSS.
   *
   * Warning: the one gradient the refresh kept on a control. It is the brand
   * mark's own tile, and the first gesture of an empty library; everything
   * else went flat so that this one still stands out. The glow is a soft,
   * offset shadow rather than a halo all around.
   */
  scanButton: cva([
    'grid size-14 -translate-y-3 place-items-center rounded-card',
    'bg-linear-135 from-accent to-accent-violet text-white',
    'shadow-[inset_0_1px_0_rgb(255_255_255/0.2),0_10px_24px_-10px_rgb(81_136_255/0.7)] transition-transform',
    'active:scale-95 focus-visible:ring-2 focus-visible:ring-accent/60 focus-visible:outline-none',
  ]),

  brand: cva(
    'flex items-center gap-2 font-display font-semibold tracking-tight text-fg',
    {
      variants: {
        layout: {
          topbar: 'text-base',
          rail: 'px-2.5 pb-4 text-base',
          tabbar: 'hidden',
        },
      },
    },
  ),

  brandDot: cva('size-6'),
};
